import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import * as authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const accessTokenRef = useRef(null);

  // Store token in ref so authenticatedFetch always has current value
  // without causing re-renders
  const setAccessToken = useCallback((token) => {
    accessTokenRef.current = token;
  }, []);

  const getAccessToken = useCallback(() => {
    return accessTokenRef.current;
  }, []);

  // Try to restore session on app load
  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      try {
        const token = await authService.refreshAccessToken();
        if (token && !cancelled) {
          setAccessToken(token);
          const userData = await authService.fetchCurrentUser(token);
          if (!cancelled) setUser(userData);
        }
      } catch {
        // No valid session — user stays logged out
      } finally {
        if (!cancelled) setAuthLoading(false);
      }
    };

    initAuth();
    return () => { cancelled = true; };
  }, [setAccessToken]);

  const login = useCallback(async (email, password) => {
    const data = await authService.login(email, password);
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, [setAccessToken]);

  const signup = useCallback(async (email, password, fullName) => {
    const data = await authService.signup(email, password, fullName);
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, [setAccessToken]);

  const logoutUser = useCallback(async () => {
    await authService.logout();
    setAccessToken(null);
    setUser(null);
  }, [setAccessToken]);

  const authedFetch = useCallback(async (url, options = {}) => {
    return authService.authenticatedFetch(
      url,
      options,
      getAccessToken,
      (newToken) => setAccessToken(newToken)
    );
  }, [getAccessToken, setAccessToken]);

  const value = {
    user,
    isAuthenticated: !!user,
    authLoading,
    login,
    signup,
    logout: logoutUser,
    authenticatedFetch: authedFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
